import { computed, onUnmounted, ref, shallowRef } from 'vue';
import type {
  BossRevolveAxisResolution,
  BossRevolveSceneContour,
  BossRevolveSessionAxisSelection,
  BossRevolveSessionSelection,
  FeatureEditSession,
  RevolveAngleDragChangePayload,
  RevolveCutPreviewFragment,
  RevolveCutPreviewFragmentMesh,
  RevolveFeatureEditDefinition,
} from '@puppycad/cad-core';
import {
  BossExtrudeSceneResolver,
  hasCurveProfileRegionGeometry,
  isSketchGroup,
  RevolveAxisProfilePlaneCompatibilityValidator,
  RevolveFeatureEditDefinitionFactory,
} from '@puppycad/cad-core';
import type { ViewportSelectionSnapshot } from '../extrude-types';
import { rebuildTrackedSketchContours } from '@/core/sketch/rebuild-tracked-sketch-contours';
import {
  buildCadCoreRevolveDefinitionSnapshot,
  cloneRevolveCutKeepShapeKeys,
  normalizeRevolveCutKeepShapeKeys,
} from './revolve-definition-snapshot';
import { createRevolveDirtyPreviewScheduler } from './revolve-dirty-preview-scheduler';
import {
  buildRevolveContourLabel,
  resolveRevolveErrorMessage,
} from './revolve-ui-state-helpers';
import { buildBossRevolveSessionContourSnapshots } from './revolve-selection-snapshot';
import { useRevolveCutKeepBodiesController } from './useRevolveCutKeepBodiesController';
import type { RevolveCutFragmentUiItem, RevolveUiState, UseRevolveFeatureOptions } from './useRevolveFeatureTypes';
import { applyFeatureCommitMeshDelta } from '../apply-feature-commit-mesh-delta';

export type { RevolveCutFragmentUiItem, RevolveUiState, UseRevolveFeatureOptions } from './useRevolveFeatureTypes';

const PREVIEW_THROTTLE_MS = 80;
const REVOLVE_MAX_ANGLE = Math.PI * 2;
const REVOLVE_ANGLE_EPS = 1e-9;
const revolveSceneContourResolver = new BossExtrudeSceneResolver();
const revolveAxisProfilePlaneValidator = new RevolveAxisProfilePlaneCompatibilityValidator();

/**
 * 旋转特征宿主侧会话 Hook。
 *
 * @param options Hook 配置。
 * @returns 旋转特征状态与动作。
 */
export function useRevolveFeature(options: UseRevolveFeatureOptions) {
  const {
    viewportRef,
    updateState,
    message,
    onPreviewLoadingChange,
  } = options;
  const featureKind = options.featureKind ?? 'boss-revolve';
  const featureLabel = featureKind === 'cut-revolve' ? '旋转切除' : '旋转凸台';
  const previewOperation = featureKind === 'cut-revolve' ? 'cut' : 'boss';

  const revolveActive = ref(false);
  const revolveDefinition = shallowRef<RevolveFeatureEditDefinition | null>(null);
  const revolveCanSubmit = ref(false);
  const revolvePreviewError = ref<string | null>(null);
  const selectedAxisLabel = ref<string | null>(null);
  const pickerTarget = ref<'axis' | 'profile' | null>(null);
  const selectedContoursVersion = ref(0);
  const restoredEditContours = ref<Array<{ id: string; label: string; sketchId?: string; contourKey?: string }>>([]);
  const cutFragments = ref<RevolveCutFragmentUiItem[]>([]);
  const cutDeletedSolidIds = ref<string[]>([]);
  const cutFragmentMeshes = shallowRef<RevolveCutPreviewFragmentMesh[]>([]);

  let pendingContours: BossRevolveSceneContour[] = [];
  let selectedAxisSelection: BossRevolveSessionAxisSelection | null = null;
  let selectedAxisResolution: BossRevolveAxisResolution | null = null;
  let selectedAxisSketchId: string | null = null;
  let editingFeatureId: string | null = null;
  let editingSession: FeatureEditSession | null = null;
  let previewTimer: number | null = null;
  let previewVersion = 0;
  let activePreviewLoadingVersion: number | null = null;
  let dragPreviewSuspended = false;
  let isPreviewMeshVisible = false;
  let unsubscribeSketchDefinitionDirtyList: Array<() => void> = [];
  const keepBodiesController = useRevolveCutKeepBodiesController({
    definition: revolveDefinition,
    cutFragments,
    submit: () => {
      void submitRevolve();
    },
    previewSelection: (selection) => {
      viewportRef.value?.updateRevolveCutKeepBodyPreviewSelection?.(selection);
    },
    clearPreviewSelection: () => {
      viewportRef.value?.clearRevolveCutKeepBodyPreview?.();
    },
  });

  /**
   * 当前旋转 PropertyManager 投影快照。
   */
  const revolveUiState = computed<RevolveUiState>(() => {
    void selectedContoursVersion.value;
    return {
      active: revolveActive.value,
      editingFeatureId,
      definition: revolveDefinition.value,
      selectedContours: editingFeatureId
        ? [...restoredEditContours.value]
        : pendingContours.map((contour, index) => ({
          id: contour.id,
          sketchId: contour.sketchId,
          contourKey: contour.contourKey,
          label: buildRevolveContourLabel(contour, index),
        })),
      selectedAxisLabel: selectedAxisLabel.value,
      pickerTarget: pickerTarget.value,
      canSubmit: revolveCanSubmit.value,
      previewError: revolvePreviewError.value,
      flipSideToCut: revolveDefinition.value?.flipSideToCut === true,
      cutFragments: [...cutFragments.value],
      cutDeletedSolidIds: [...cutDeletedSolidIds.value],
      keepBodiesDialog: keepBodiesController.dialogState.value,
    };
  });

  /**
   * 标记所选轮廓发生变化。
   */
  const markSelectedContoursChanged = (): void => {
    selectedContoursVersion.value += 1;
  };

  /**
   * 判断当前旋转 profile 是否包含曲线轮廓。
   *
   * @returns 当前选择包含圆、椭圆、圆弧、样条、贝塞尔或 B 样条时返回 true。
   */
  const hasCurveProfileRevolveSelection = (): boolean => (
    pendingContours.some((contour) => hasCurveProfileRegionGeometry(contour.geometry))
  );

  /**
   * 关闭旋转预览 loading。
   *
   * @param requestVersion 指定请求版本时，仅关闭仍属于该版本的 loading。
   */
  const hidePreviewLoading = (requestVersion?: number): void => {
    if (requestVersion !== undefined && activePreviewLoadingVersion !== requestVersion) {
      return;
    }
    if (activePreviewLoadingVersion === null) {
      return;
    }
    activePreviewLoadingVersion = null;
    onPreviewLoadingChange?.({
      active: false,
      operation: previewOperation,
      reason: 'curve-profile-preview',
    });
  };

  /**
   * 为耗时的曲线 profile 旋转预览打开 loading。
   *
   * @param requestVersion 当前预览请求版本。
   */
  const showPreviewLoadingIfNeeded = (requestVersion: number): void => {
    if (!hasCurveProfileRevolveSelection()) {
      hidePreviewLoading();
      return;
    }
    activePreviewLoadingVersion = requestVersion;
    onPreviewLoadingChange?.({
      active: true,
      operation: previewOperation,
      reason: 'curve-profile-preview',
    });
  };

  /**
   * 同步旋转区域填充可见性。
   *
   * 设计说明：
   * 预览体已经表达了当前轮廓的实体结果，此时继续显示草图区域填充会与预览端面共面闪烁；
   * 因此这里只隐藏填充，不清空 selected 区域，确保后续继续点选轮廓时拾取层仍然存在。
   */
  const syncRevolveRegionFillVisibility = (): void => {
    viewportRef.value?.setRevolveRegionFillVisible?.(!isPreviewMeshVisible);
  };

  /**
   * 清理当前预览。
   */
  const clearPreview = (): void => {
    previewVersion += 1;
    dirtyPreviewScheduler.clear();
    if (previewTimer !== null) {
      clearTimeout(previewTimer);
      previewTimer = null;
    }
    isPreviewMeshVisible = false;
    syncRevolveRegionFillVisibility();
    hidePreviewLoading();
    void viewportRef.value?.setExtrudePreviewMesh(null);
    viewportRef.value?.clearRevolveCutKeepBodyPreview?.();
  };

  /**
   * 同步旋转区域选择高亮。
   */
  const syncRevolveRegionSelections = (): void => {
    viewportRef.value?.setRevolveRegionSelections?.(pendingContours.map((contour) => contour.id));
    syncRevolveRegionFillVisibility();
  };

  /**
   * 按当前旋转轴所在草图开启区域选择。
   *
   * 设计说明：
   * 1. 相交闭合区域需要先知道目标草图，才能在 cad-core Viewer 中构建可拾取区域层；
   * 2. 草图轴场景下区域选择与 SolidWorks 行为一致，用户可直接点击阴影闭合区域；
   * 3. 模型边轴场景暂保持原有草图实体选择链路，避免在没有草图上下文时误建全局区域层。
   */
  const enableRevolveRegionSelectionForCurrentAxis = async (): Promise<void> => {
    if (!selectedAxisSketchId) {
      return;
    }
    await viewportRef.value?.enableRevolveRegionSelection?.(selectedAxisSketchId);
    syncRevolveRegionSelections();
    syncRevolveRegionFillVisibility();
  };

  /**
   * 关闭旋转区域选择。
   */
  const disableRevolveRegionSelection = (): void => {
    viewportRef.value?.disableRevolveRegionSelection?.();
  };

  /**
   * 解除草图定义 dirty 订阅。
   */
  const unbindSketchDefinitionDirty = (): void => {
    unsubscribeSketchDefinitionDirtyList.forEach((unsubscribe) => {
      unsubscribe();
    });
    unsubscribeSketchDefinitionDirtyList = [];
  };

  /**
   * 读取当前会话需要跟踪的草图 ID。
   *
   * @returns 去重后的草图 ID 列表。
   */
  const collectTrackedSketchIds = (): string[] => {
    const sketchIds = new Set<string>();
    const contourSketchId = pendingContours[0]?.sketchId;
    if (typeof contourSketchId === 'string' && contourSketchId.length > 0) {
      sketchIds.add(contourSketchId);
    }
    if (typeof selectedAxisSketchId === 'string' && selectedAxisSketchId.length > 0) {
      sketchIds.add(selectedAxisSketchId);
    }
    return Array.from(sketchIds);
  };

  /**
   * 从旋转轴解析结果中读取草图 ID。
   *
   * @param axis 旋转轴解析结果。
   * @returns 轴所属草图 ID；模型边轴返回 `null`。
   */
  const resolveAxisSketchId = (axis: BossRevolveAxisResolution): string | null => {
    const axisReference = axis.axisReference;
    if (axisReference?.kind !== 'sketch_edge') {
      return null;
    }
    /**
     * binding 保留 persistentId，旋转会话中的区域层、可见性与 dirty 订阅只使用 runtime UUID。
     * 在会话入口完成一次换算，避免同一草图以两种 ID 重复订阅或产生不可见区域层。
     */
    return viewportRef.value?.resolveRuntimeSketchId?.(
      axisReference.sourceRef.ownerSketchId,
    ) ?? null;
  };

  /**
   * 校验旋转轴是否能用于指定轮廓集合。
   *
   * @param axis 旋转轴解析结果。
   * @param contours 轮廓集合。
   * @returns 校验失败时返回提示文案；通过时返回 null。
   */
  const resolveAxisProfilePlaneError = (
    axis: BossRevolveAxisResolution | null,
    contours: BossRevolveSceneContour[],
  ): string | null => {
    if (!axis || contours.length === 0) {
      return null;
    }
    const compatibility = revolveAxisProfilePlaneValidator.validate({
      axis,
      contours,
    });
    if (compatibility.compatible === false) {
      return compatibility.message;
    }
    return null;
  };

  /**
   * 判断候选选择是否满足旋转轴与轮廓草图平面关系。
   *
   * @param axis 旋转轴解析结果。
   * @param contours 轮廓集合。
   * @param notify 是否显示消息提示。
   * @param invalidateCurrentDraft 是否把当前命令草稿置为不可提交。
   * @returns 可用于当前旋转命令时返回 true。
   */
  const validateAxisProfilePlaneSelection = (
    axis: BossRevolveAxisResolution | null,
    contours: BossRevolveSceneContour[],
    notify: boolean,
    invalidateCurrentDraft: boolean,
  ): boolean => {
    const errorMessage = resolveAxisProfilePlaneError(axis, contours);
    if (!errorMessage) {
      return true;
    }

    if (notify) {
      message.warning(errorMessage);
    }
    if (invalidateCurrentDraft) {
      // 没有旧的合法选择可保留时，才把当前命令草稿置为失败态；替换选择失败时应保留原有预览。
      revolvePreviewError.value = errorMessage;
      revolveCanSubmit.value = false;
      clearCutPreviewMetadata();
      stopRevolveAngleDrag();
      clearPreview();
    }
    return false;
  };

  /**
   * 构建创建态选择草稿。
   *
   * @returns 创建态选择草稿；缺少必要选择时返回 null。
   */
  const buildCreateSelection = (): BossRevolveSessionSelection | null => {
    if (!selectedAxisSelection || pendingContours.length === 0) {
      return null;
    }
    return {
      axis: selectedAxisSelection,
      contourIds: pendingContours.map((contour) => contour.id),
      contours: buildBossRevolveSessionContourSnapshots(pendingContours),
    };
  };

  /**
   * 停止旋转角度拖拽手柄。
   */
  const stopRevolveAngleDrag = (): void => {
    viewportRef.value?.stopBossRevolveAngleDrag?.();
  };

  /**
   * 同步旋转切除预览元数据。
   *
   * @param meshData 预览网格。
   */
  const syncCutPreviewMetadata = (meshData: unknown): void => {
    if (featureKind !== 'cut-revolve' || !revolveDefinition.value) {
      return;
    }
    const metadata = (meshData as { metadata?: Record<string, unknown> } | null)?.metadata ?? {};
    const fragments = Array.isArray(metadata.cutRevolveFragments)
      ? metadata.cutRevolveFragments as RevolveCutPreviewFragment[]
      : [];
    const previousSelection = cloneRevolveCutKeepShapeKeys(revolveDefinition.value.keepShapeKeys ?? {});
    const nextSelection = normalizeRevolveCutKeepShapeKeys(fragments, previousSelection);
    revolveDefinition.value = {
      ...revolveDefinition.value,
      previewToken: typeof metadata.cutRevolvePreviewToken === 'string'
        ? metadata.cutRevolvePreviewToken
        : undefined,
      keepShapeKeys: nextSelection,
    };
    cutFragments.value = fragments.map((fragment) => ({
      ...fragment,
      selected: (nextSelection[fragment.sourceSolidId] ?? []).includes(fragment.shapeKey),
    }));
    cutDeletedSolidIds.value = Array.isArray(metadata.cutRevolveDeletedSolidIds)
      ? metadata.cutRevolveDeletedSolidIds.filter((item): item is string => typeof item === 'string')
      : [];
  };

  /**
   * 同步旋转切除保留实体高亮网格。
   *
   * @param fragmentMeshes 片段高亮网格。
   */
  const syncCutPreviewFragmentMeshes = (
    fragmentMeshes: RevolveCutPreviewFragmentMesh[] | undefined,
  ): void => {
    if (featureKind !== 'cut-revolve') {
      return;
    }
    cutFragmentMeshes.value = Array.isArray(fragmentMeshes) ? fragmentMeshes : [];
    viewportRef.value?.setRevolveCutKeepBodyPreviewFragments?.(cutFragmentMeshes.value);
  };

  /**
   * 清理旋转切除预览元数据。
   */
  const clearCutPreviewMetadata = (): void => {
    cutFragments.value = [];
    cutDeletedSolidIds.value = [];
    cutFragmentMeshes.value = [];
    viewportRef.value?.clearRevolveCutKeepBodyPreview?.();
    keepBodiesController.clear();
  };

  /**
   * 基于场景中的最新草图轮廓重建当前已选旋转轮廓。
   *
   * 设计说明：
   * 1. 旋转预览依赖草图 profile，草图点位变化后不能继续沿用旧轮廓对象；
   * 2. 这里复用 profile 特征的稳定键匹配规则，只替换为最新场景轮廓；
   * 3. 当轮廓已失效时统一清理预览和提交态，由属性栏顶部提示用户重新选择。
   *
   * @param sketchId 发生 dirty 的草图 ID。
   * @returns 当前轮廓是否仍然可用于预览。
   */
  const rebuildPendingContoursFromScene = (sketchId: string): boolean => {
    if (pendingContours.length === 0 || pendingContours[0]?.sketchId !== sketchId) {
      return true;
    }

    const rebuiltFromSelectionIds = pendingContours
      .map((contour) => viewportRef.value?.resolveBossRevolveSelectionContours?.(contour.id) ?? null)
      .filter((contours): contours is BossRevolveSceneContour[] => Array.isArray(contours) && contours.length > 0)
      .flat();
    if (
      rebuiltFromSelectionIds.length === pendingContours.length
      && rebuiltFromSelectionIds.every((contour) => contour.sketchId === sketchId)
    ) {
      pendingContours = rebuiltFromSelectionIds;
      markSelectedContoursChanged();
      syncRevolveRegionSelections();
      return true;
    }

    const scene = viewportRef.value?.getScene?.() ?? null;
    const sketchGroup = scene?.getObjectByProperty('uuid', sketchId) ?? null;
    if (!sketchGroup || !isSketchGroup(sketchGroup)) {
      // 场景暂不可读时不贸然丢弃选择，后续预览入口会按现有选择给出权威结果。
      return true;
    }

    const latestContours = revolveSceneContourResolver.collectContoursFromGroup(sketchGroup);
    const rebuiltContours = rebuildTrackedSketchContours(pendingContours, latestContours);
    if (rebuiltContours.length === 0) {
      pendingContours = [];
      markSelectedContoursChanged();
      revolveCanSubmit.value = false;
      revolvePreviewError.value = '当前草图未形成可用于旋转的已选轮廓。';
      clearCutPreviewMetadata();
      stopRevolveAngleDrag();
      clearPreview();
      return false;
    }

    pendingContours = rebuiltContours;
    markSelectedContoursChanged();
    syncRevolveRegionSelections();
    return true;
  };

  /**
   * 处理已合并的草图 definition dirty 事件。
   *
   * @param sketchIds 发生变化的草图 ID 列表。
   */
  const flushSketchDefinitionDirty = (sketchIds: string[]): void => {
    void flushSketchDefinitionDirtyAsync(sketchIds);
  };

  /**
   * 异步处理已合并的草图 definition dirty 事件。
   * @param sketchIds 发生变化的草图 ID 列表。
   */
  const flushSketchDefinitionDirtyAsync = async (sketchIds: string[]): Promise<void> => {
    if (!revolveActive.value || editingFeatureId) {
      return;
    }

    await Promise.all(sketchIds.map(async (sketchId) => {
      await viewportRef.value?.refreshRevolveRegionsForSketch?.(sketchId);
    }));

    const contoursStillValid = sketchIds.every((sketchId) => rebuildPendingContoursFromScene(sketchId));
    bindSketchDefinitionDirty();
    if (!contoursStillValid) {
      return;
    }

    syncRevolveAngleDrag();
    refreshPreview(true);
  };

  const dirtyPreviewScheduler = createRevolveDirtyPreviewScheduler({
    delayMs: PREVIEW_THROTTLE_MS,
    flush: flushSketchDefinitionDirty,
  });

  /**
   * 响应草图 definition dirty 事件。
   *
   * @param sketchId 发生变化的草图 ID。
   */
  const handleSketchDefinitionDirty = (sketchId: string): void => {
    if (!revolveActive.value || editingFeatureId) {
      return;
    }
    dirtyPreviewScheduler.schedule(sketchId);
  };

  /**
   * 订阅当前旋转会话依赖的草图 definition dirty 事件。
   */
  function bindSketchDefinitionDirty(): void {
    unbindSketchDefinitionDirty();
    if (!revolveActive.value || editingFeatureId) {
      return;
    }

    const subscribeSketchDefinitionDirty = viewportRef.value?.subscribeSketchDefinitionDirty;
    if (!subscribeSketchDefinitionDirty) {
      return;
    }

    unsubscribeSketchDefinitionDirtyList = collectTrackedSketchIds().map((sketchId) => (
      subscribeSketchDefinitionDirty(sketchId, () => {
        handleSketchDefinitionDirty(sketchId);
      })
    ));
  }

  /**
   * 校验当前旋转定义是否可执行。
   *
   * @param definition 当前旋转编辑定义。
   * @returns 是否通过校验。
   */
  const validateDefinition = (definition: RevolveFeatureEditDefinition): boolean => {
    if (definition.direction1Angle <= 0) {
      revolvePreviewError.value = '方向1角度必须大于 0';
      revolveCanSubmit.value = false;
      return false;
    }
    if (
      definition.direction1Angle
      + (definition.direction2Enabled ? definition.direction2Angle : 0)
      > REVOLVE_MAX_ANGLE + REVOLVE_ANGLE_EPS
    ) {
      revolvePreviewError.value = '方向1与方向2角度之和不能超过 360 度';
      revolveCanSubmit.value = false;
      return false;
    }
    if (!editingFeatureId && !selectedAxisSelection) {
      revolvePreviewError.value = '请选择旋转轴。';
      revolveCanSubmit.value = false;
      return false;
    }
    if (!editingFeatureId && pendingContours.length === 0) {
      revolvePreviewError.value = '请选择闭合轮廓。';
      revolveCanSubmit.value = false;
      return false;
    }
    const axisProfilePlaneError = resolveAxisProfilePlaneError(selectedAxisResolution, pendingContours);
    if (!editingFeatureId && axisProfilePlaneError) {
      revolvePreviewError.value = axisProfilePlaneError;
      revolveCanSubmit.value = false;
      return false;
    }

    revolvePreviewError.value = null;
    revolveCanSubmit.value = true;
    return true;
  };

  /**
   * 刷新当前旋转预览。
   *
   * @param force 是否跳过节流立即刷新。
   */
  const refreshPreview = (force = false): void => {
    const definition = revolveDefinition.value;
    if (!revolveActive.value || !definition) {
      return;
    }
    if (dragPreviewSuspended) {
      return;
    }
    if (!validateDefinition(definition)) {
      clearPreview();
      return;
    }
    const definitionSnapshot = buildCadCoreRevolveDefinitionSnapshot(definition);

    const currentVersion = ++previewVersion;
    showPreviewLoadingIfNeeded(currentVersion);
    if (previewTimer !== null) {
      clearTimeout(previewTimer);
      previewTimer = null;
    }

    const run = async (): Promise<void> => {
      const selection = editingFeatureId ? undefined : buildCreateSelection();
      if (!editingFeatureId && !selection) {
        clearPreview();
        return;
      }
      try {
        const previewResult = featureKind === 'cut-revolve'
          ? await viewportRef.value?.previewCutRevolveFeature?.({
            featureId: editingFeatureId,
            selection,
            definition: definitionSnapshot,
          })
          : await viewportRef.value?.previewBossRevolveFeature?.({
            featureId: editingFeatureId,
            selection,
            definition: definitionSnapshot,
          });
        if (!previewResult || currentVersion !== previewVersion) {
          hidePreviewLoading(currentVersion);
          return;
        }
        syncCutPreviewFragmentMeshes(previewResult.fragmentMeshes);
        syncCutPreviewMetadata(previewResult.meshData);
        await viewportRef.value?.setExtrudePreviewMesh(previewResult.meshData, {
          prototype: previewResult.prototype,
          draftDefinition: previewResult.draftDefinition as unknown,
        });
        isPreviewMeshVisible = true;
        syncRevolveRegionFillVisibility();
        hidePreviewLoading(currentVersion);
      } catch (error) {
        if (currentVersion !== previewVersion) {
          return;
        }
        hidePreviewLoading(currentVersion);
        revolvePreviewError.value = resolveRevolveErrorMessage(error);
        revolveCanSubmit.value = false;
        clearCutPreviewMetadata();
        isPreviewMeshVisible = false;
        syncRevolveRegionFillVisibility();
        void viewportRef.value?.setExtrudePreviewMesh(null);
      }
    };

    if (force) {
      void run();
      return;
    }

    previewTimer = window.setTimeout(() => {
      previewTimer = null;
      void run();
    }, PREVIEW_THROTTLE_MS);
  };

  /**
   * 响应旋转角度手柄拖拽。
   *
   * @param payload 拖拽角度载荷。
   */
  const handleRevolveAngleDragChange = (payload: RevolveAngleDragChangePayload): void => {
    const currentDefinition = revolveDefinition.value;
    if (!revolveActive.value || !currentDefinition) {
      return;
    }
    revolveDefinition.value = {
      ...currentDefinition,
      direction1Angle: payload.direction1Angle,
      directionReversed: payload.directionReversed,
      direction2Enabled: payload.direction2Enabled,
      direction2Angle: payload.direction2Angle,
    };
    if (payload.isDragging) {
      dragPreviewSuspended = true;
      validateDefinition(revolveDefinition.value);
      return;
    }
    dragPreviewSuspended = false;
    syncRevolveAngleDrag();
    refreshPreview(true);
  };

  /**
   * 同步旋转角度拖拽手柄。
   *
   * @returns 是否成功展示手柄。
   */
  const syncRevolveAngleDrag = (): boolean => {
    const definition = revolveDefinition.value;
    if (!revolveActive.value || !definition) {
      stopRevolveAngleDrag();
      return false;
    }
    const selection = editingFeatureId ? undefined : buildCreateSelection();
    if (!editingFeatureId && !selection) {
      stopRevolveAngleDrag();
      return false;
    }
    return viewportRef.value?.updateBossRevolveAngleDrag?.({
      featureId: editingFeatureId,
      selection,
      definition: buildCadCoreRevolveDefinitionSnapshot(definition),
      onAnglesChange: handleRevolveAngleDragChange,
    }) ?? false;
  };

  /**
   * 结束当前旋转会话。
   *
   * @param input 收尾配置。
   * @returns 是否实际结束了旋转会话。
   */
  const finalizeRevolveSession = (input: { restoreFeature: boolean }): boolean => {
    if (!revolveActive.value) {
      return false;
    }
    if (input.restoreFeature && editingFeatureId && editingSession) {
      viewportRef.value?.closeFeatureEditSession?.(editingFeatureId);
    }
    editingFeatureId = null;
    editingSession = null;
    pendingContours = [];
    selectedAxisSelection = null;
    selectedAxisResolution = null;
    selectedAxisSketchId = null;
    restoredEditContours.value = [];
    selectedAxisLabel.value = null;
    pickerTarget.value = null;
    revolveActive.value = false;
    revolveDefinition.value = null;
    revolveCanSubmit.value = false;
    revolvePreviewError.value = null;
    clearCutPreviewMetadata();
    markSelectedContoursChanged();
    dragPreviewSuspended = false;
    unbindSketchDefinitionDirty();
    disableRevolveRegionSelection();
    stopRevolveAngleDrag();
    clearPreview();
    return true;
  };

  /**
   * 启动旋转凸台创建命令。
   */
  const handleRevolveCommand = async (): Promise<void> => {
    editingFeatureId = null;
    editingSession = null;
    pendingContours = [];
    selectedAxisSelection = null;
    selectedAxisResolution = null;
    selectedAxisSketchId = null;
    restoredEditContours.value = [];
    selectedAxisLabel.value = null;
    pickerTarget.value = 'axis';
    revolveDefinition.value = RevolveFeatureEditDefinitionFactory.createDefault();
    revolveActive.value = true;
    revolveCanSubmit.value = false;
    revolvePreviewError.value = '请选择旋转轴。';
    clearCutPreviewMetadata();
    markSelectedContoursChanged();
    dragPreviewSuspended = false;
    unbindSketchDefinitionDirty();
    disableRevolveRegionSelection();
    stopRevolveAngleDrag();
    clearPreview();
    viewportRef.value?.enableHover?.('crosshair');
    message.info('请选择草图线段或模型直线边作为旋转轴。');
  };

  /**
   * 处理视口选择变化。
   *
   * @param selection 当前选择快照。
   * @returns 是否已被旋转命令消费。
   */
  const handleRevolveSelectionChange = async (
    selection: ViewportSelectionSnapshot | string | null,
  ): Promise<boolean> => {
    if (!revolveActive.value || editingFeatureId) {
      return false;
    }

    const meshId = typeof selection === 'string'
      ? selection
      : selection?.objectId ?? null;
    const topologyPick = typeof selection === 'string'
      ? null
      : selection?.topologyPick ?? null;
    if (!meshId) {
      return true;
    }

    if (pickerTarget.value === 'axis') {
      const axis = viewportRef.value?.resolveBossRevolveAxisSelection?.({
        objectId: meshId,
        topologyPick,
      }) ?? null;
      if (!axis) {
        message.warning('旋转轴仅支持草图线段或模型直线边。');
        return true;
      }
      if (!validateAxisProfilePlaneSelection(axis, pendingContours, true, !selectedAxisResolution)) {
        return true;
      }
      selectedAxisSelection = {
        objectId: axis.sourceObjectId,
        topologyPick,
      };
      selectedAxisResolution = axis;
      selectedAxisSketchId = resolveAxisSketchId(axis);
      selectedAxisLabel.value = axis.label;
      pickerTarget.value = 'profile';
      validateDefinition(revolveDefinition.value ?? RevolveFeatureEditDefinitionFactory.createDefault());
      bindSketchDefinitionDirty();
      await enableRevolveRegionSelectionForCurrentAxis();
      syncRevolveAngleDrag();
      refreshPreview(true);
      message.info('请选择闭合轮廓。');
      return true;
    }

    if (pickerTarget.value !== 'profile') {
      return false;
    }

    const resolvedContours = viewportRef.value?.resolveBossRevolveSelectionContours?.(meshId) ?? null;
    if (resolvedContours === 'need-region') {
      message.info('检测到多个闭合轮廓，请直接点击目标轮廓区域。');
      return true;
    }
    if (resolvedContours === 'open-sketch') {
      message.warning('当前草图未形成闭合轮廓，无法进行旋转凸台。');
      return true;
    }
    if (!Array.isArray(resolvedContours) || resolvedContours.length === 0) {
      return false;
    }

    const sketchId = resolvedContours[0]!.sketchId;
    const switchedSketch = pendingContours.length > 0 && pendingContours[0]!.sketchId !== sketchId;
    const nextPendingContours = switchedSketch ? [] : [...pendingContours];
    resolvedContours.forEach((contour) => {
      const existedIndex = nextPendingContours.findIndex((item) => item.id === contour.id);
      if (existedIndex >= 0) {
        nextPendingContours.splice(existedIndex, 1);
        return;
      }
      nextPendingContours.push(contour);
    });
    if (!validateAxisProfilePlaneSelection(selectedAxisResolution, nextPendingContours, true, pendingContours.length === 0)) {
      return true;
    }
    pendingContours = nextPendingContours;
    if (switchedSketch) {
      message.info('多轮廓旋转仅支持同一草图，已切换到当前草图。');
    }
    markSelectedContoursChanged();
    syncRevolveRegionSelections();
    validateDefinition(revolveDefinition.value ?? RevolveFeatureEditDefinitionFactory.createDefault());
    bindSketchDefinitionDirty();
    syncRevolveAngleDrag();
    refreshPreview(true);
    return true;
  };

  /**
   * 启动旋转特征编辑会话。
   *
   * @param featureId 目标特征 ID。
   * @returns 是否成功进入编辑态。
   */
  const startEditingRevolveFeature = async (featureId: string): Promise<boolean> => {
    const payload = featureKind === 'cut-revolve'
      ? await viewportRef.value?.startCutRevolveEditSession?.(featureId)
      : await viewportRef.value?.startBossRevolveEditSession?.(featureId);
    if (!payload?.definition || !payload.session) {
      return false;
    }

    editingFeatureId = featureId;
    editingSession = payload.session;
    pendingContours = [];
    selectedAxisSelection = null;
    selectedAxisResolution = null;
    selectedAxisSketchId = null;
    restoredEditContours.value = payload.selectedContours.map((contour) => ({ ...contour }));
    selectedAxisLabel.value = payload.selectedAxisLabel;
    pickerTarget.value = null;
    revolveActive.value = true;
    revolveDefinition.value = payload.definition;
    revolveCanSubmit.value = true;
    revolvePreviewError.value = null;
    clearCutPreviewMetadata();
    markSelectedContoursChanged();
    dragPreviewSuspended = false;
    unbindSketchDefinitionDirty();
    disableRevolveRegionSelection();
    syncRevolveAngleDrag();
    refreshPreview(true);
    return true;
  };

  /**
   * 提交当前旋转特征。
   */
  const submitRevolve = async (): Promise<void> => {
    const definition = revolveDefinition.value;
    if (!definition || !validateDefinition(definition)) {
      if (revolvePreviewError.value) {
        message.warning(revolvePreviewError.value);
      }
      return;
    }

    const selection = editingFeatureId ? undefined : buildCreateSelection();
    if (!editingFeatureId && !selection) {
      message.warning('旋转参数不完整。');
      return;
    }

    if (featureKind === 'cut-revolve' && cutFragments.value.length > 0 && !keepBodiesController.isSubmitArmed()) {
      keepBodiesController.open();
      return;
    }
    keepBodiesController.resetSubmitArmed();

    const wasEditing = Boolean(editingFeatureId);
    clearPreview();
    const commitResult = featureKind === 'cut-revolve'
      ? await viewportRef.value?.commitCutRevolveFeature?.({
        featureId: editingFeatureId,
        selection,
        definition: buildCadCoreRevolveDefinitionSnapshot(definition),
      })
      : await viewportRef.value?.commitBossRevolveFeature?.({
        featureId: editingFeatureId,
        selection,
        definition: buildCadCoreRevolveDefinitionSnapshot(definition),
      });
    if (!commitResult) {
      throw new Error(`${featureLabel}提交通道不可用。`);
    }

    const committedFeatureId = editingFeatureId ?? commitResult.featureId;
    editingFeatureId = null;
    editingSession = null;
    if (!commitResult.replaced) {
      await applyFeatureCommitMeshDelta(viewportRef, commitResult);
    }
    await updateState();
    finalizeRevolveSession({ restoreFeature: false });
    if (committedFeatureId) {
      message.success(wasEditing ? `${featureLabel}已更新。` : `${featureLabel}已创建。`);
    }
  };

  /**
   * 取消当前旋转会话。
   *
   * @returns 是否实际取消。
   */
  const cancelRevolve = (): boolean => {
    return finalizeRevolveSession({ restoreFeature: true });
  };

  /**
   * 激活属性栏选择目标。
   *
   * @param target 选择目标。
   */
  const activateRevolvePicker = (target: 'axis' | 'profile'): void => {
    if (!revolveActive.value || editingFeatureId) {
      return;
    }
    pickerTarget.value = target;
    if (target === 'profile') {
      void enableRevolveRegionSelectionForCurrentAxis();
    } else {
      disableRevolveRegionSelection();
    }
    message.info(target === 'axis' ? '请选择旋转轴。' : '请选择闭合轮廓。');
  };

  /**
   * 清空已选旋转轴。
   */
  const clearRevolveSelectedAxis = (): void => {
    if (editingFeatureId) {
      return;
    }
    selectedAxisSelection = null;
    selectedAxisResolution = null;
    selectedAxisSketchId = null;
    selectedAxisLabel.value = null;
    pickerTarget.value = 'axis';
    validateDefinition(revolveDefinition.value ?? RevolveFeatureEditDefinitionFactory.createDefault());
    clearCutPreviewMetadata();
    bindSketchDefinitionDirty();
    disableRevolveRegionSelection();
    stopRevolveAngleDrag();
    clearPreview();
  };

  /**
   * 清空已选轮廓。
   */
  const clearRevolveSelectedContours = (): void => {
    if (editingFeatureId) {
      return;
    }
    pendingContours = [];
    pickerTarget.value = 'profile';
    markSelectedContoursChanged();
    syncRevolveRegionSelections();
    validateDefinition(revolveDefinition.value ?? RevolveFeatureEditDefinitionFactory.createDefault());
    clearCutPreviewMetadata();
    bindSketchDefinitionDirty();
    stopRevolveAngleDrag();
    clearPreview();
  };

  /**
   * 移除指定轮廓。
   *
   * @param id 轮廓 ID。
   */
  const removeRevolveSelectedContour = (id: string): void => {
    if (editingFeatureId) {
      return;
    }
    const nextContours = pendingContours.filter((contour) => contour.id !== id);
    if (nextContours.length === pendingContours.length) {
      return;
    }
    pendingContours = nextContours;
    markSelectedContoursChanged();
    syncRevolveRegionSelections();
    validateDefinition(revolveDefinition.value ?? RevolveFeatureEditDefinitionFactory.createDefault());
    bindSketchDefinitionDirty();
    syncRevolveAngleDrag();
    refreshPreview(true);
  };

  /**
   * 高亮指定轮廓。
   *
   * @param _id 轮廓 ID。
   */
  const highlightRevolveSelectedContour = (id: string | null): void => {
    viewportRef.value?.setRevolveRegionHover?.(id);
  };

  /**
   * 高亮当前旋转轴。
   *
   * @param _enabled 是否高亮。
   */
  const highlightRevolveSelectedAxis = (_enabled: boolean): void => {
    // 当前新版 Viewer 暂无独立旋转轴高亮层；后续可接入统一 SelectionItem 高亮。
  };

  /**
   * 设置方向 1 角度。
   *
   * @param angle 新角度值（弧度）。
   */
  const setRevolveDirection1Angle = (angle: number): void => {
    if (!revolveDefinition.value) {
      return;
    }
    revolveDefinition.value = {
      ...revolveDefinition.value,
      direction1Angle: angle,
    };
    syncRevolveAngleDrag();
    refreshPreview();
  };

  /**
   * 设置方向 2 启用状态。
   *
   * @param enabled 是否启用方向 2。
   */
  const setRevolveDirection2Enabled = (enabled: boolean): void => {
    if (!revolveDefinition.value) {
      return;
    }
    revolveDefinition.value = {
      ...revolveDefinition.value,
      direction2Enabled: enabled,
      direction2Angle: enabled ? revolveDefinition.value.direction2Angle : 0,
    };
    syncRevolveAngleDrag();
    refreshPreview();
  };

  /**
   * 设置方向 2 角度。
   *
   * @param angle 新角度值（弧度）。
   */
  const setRevolveDirection2Angle = (angle: number): void => {
    if (!revolveDefinition.value) {
      return;
    }
    revolveDefinition.value = {
      ...revolveDefinition.value,
      direction2Angle: angle,
    };
    syncRevolveAngleDrag();
    refreshPreview();
  };

  /**
   * 设置主方向反向状态。
   *
   * @param reversed 是否反向。
   */
  const setRevolveDirectionReversed = (reversed: boolean): void => {
    if (!revolveDefinition.value) {
      return;
    }
    revolveDefinition.value = {
      ...revolveDefinition.value,
      directionReversed: reversed,
    };
    syncRevolveAngleDrag();
    refreshPreview();
  };

  /**
   * 设置旋转切除翻转切除侧。
   *
   * @param enabled 是否翻转切除侧。
   */
  const setRevolveFlipSideToCut = (enabled: boolean): void => {
    if (!revolveDefinition.value) {
      return;
    }
    revolveDefinition.value = {
      ...revolveDefinition.value,
      flipSideToCut: enabled,
    };
    syncRevolveAngleDrag();
    refreshPreview(true);
  };

  /**
   * 切换旋转切除保留体片段。
   *
   * @param sourceSolidId 来源实体 ID。
   * @param shapeKey 片段稳定键。
   */
  const toggleRevolveCutFragment = (sourceSolidId: string, shapeKey: string): void => {
    if (!revolveDefinition.value || featureKind !== 'cut-revolve') {
      return;
    }
    const nextSelection = cloneRevolveCutKeepShapeKeys(revolveDefinition.value.keepShapeKeys ?? {});
    const current = new Set(nextSelection[sourceSolidId] ?? []);
    if (current.has(shapeKey)) {
      current.delete(shapeKey);
    } else {
      current.add(shapeKey);
    }
    nextSelection[sourceSolidId] = Array.from(current);
    revolveDefinition.value = {
      ...revolveDefinition.value,
      keepShapeKeys: nextSelection,
    };
    cutFragments.value = cutFragments.value.map((fragment) => (
      fragment.sourceSolidId === sourceSolidId && fragment.shapeKey === shapeKey
        ? { ...fragment, selected: current.has(shapeKey) }
        : fragment
    ));
    refreshPreview(true);
  };

  onUnmounted(() => {
    cancelRevolve();
  });

  return {
    revolveUiState,
    handleRevolveCommand,
    handleRevolveSelectionChange,
    startEditingRevolveFeature,
    submitRevolve,
    cancelRevolve,
    activateRevolvePicker,
    clearRevolveSelectedAxis,
    clearRevolveSelectedContours,
    removeRevolveSelectedContour,
    highlightRevolveSelectedAxis,
    highlightRevolveSelectedContour,
    setRevolveDirection1Angle,
    setRevolveDirection2Enabled,
    setRevolveDirection2Angle,
    setRevolveDirectionReversed,
    setRevolveFlipSideToCut,
    toggleRevolveCutFragment,
    setRevolveCutKeepBodiesSelectionMode: keepBodiesController.setSelectionMode,
    toggleRevolveCutKeepBodiesDraftFragment: keepBodiesController.toggleDraftFragment,
    confirmRevolveCutKeepBodiesDialog: keepBodiesController.confirm,
    cancelRevolveCutKeepBodiesDialog: keepBodiesController.cancel,
  };
}
